import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import AgGridCustomTable, { TableProps } from "../../../../util/component/AgGridCustomTable";
import useOperationTranslation from "../../hooks/useOperationTranslation";

interface EmptyStateData {
  title?: string,
  content?: string,
}

interface Props<TData> extends TableProps<TData> {
  totalElements?: number;
  emptyState?: EmptyStateData;
}

const AgGridOperationTable = <TData extends any = any>(props: Props<TData>) => {
  const {gridRef, totalElements, emptyState, paginationPageSize, ...gridProps} = props;

  const newGridRef = useRef<AgGridReact<TData>>(null);

  const { i18n } = useTranslation();
  const t = useOperationTranslation();

  const defaultColDef: ColDef = {
    resizable: true,
  }

  const localGridRef = useMemo(() => {
    return gridRef || newGridRef;
  }, []);

  useEffect(() => {
    if (!localGridRef || !localGridRef.current || !localGridRef.current.api) {
      return;
    }

    if (totalElements === 0) {
      localGridRef.current.api.showNoRowsOverlay();
    } else {
      localGridRef.current.api.hideOverlay();
    }
  }, [totalElements, i18n.language, gridProps.datasource, gridProps.serverSideDatasource]);

  const NoRowsOverlayComponent = (params: ICellRendererParams<TData> & EmptyStateData) => {
    const title = params?.title || t("emptyStateGrid.title");
    const content = params?.content || t("emptyStateGrid.content");
    return (
      <Mui.Stack marginTop="-60px">
        <Mui.Typography variant="h6">
          {title}
        </Mui.Typography>

        {content && (
          <Mui.Typography>
            {content}
          </Mui.Typography>
        )}
      </Mui.Stack>
    )
  };

  return (
    <AgGridCustomTable
      gridRef={localGridRef}
      cacheBlockSize={props.cacheBlockSize || 10}
      rowModelType={props.rowModelType || "serverSide"}
      serverSideInfiniteScroll={props.serverSideInfiniteScroll || true}
      noRowsOverlayComponent={NoRowsOverlayComponent}
      noRowsOverlayComponentParams={emptyState}
      defaultColDef={defaultColDef}
	  paginationPageSize={paginationPageSize || 10}
      {...gridProps}
    />
  )
}

export default AgGridOperationTable;